import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  FileText, 
  Calendar,
  Users,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Plus,
  MapPin
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const statusColors = {
  pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-500', label: 'En attente' },
  accepted: { bg: 'bg-green-500/20', text: 'text-green-500', label: 'Acceptée' },
  rejected: { bg: 'bg-red-500/20', text: 'text-red-500', label: 'Refusée' },
  waitlist: { bg: 'bg-blue-500/20', text: 'text-blue-500', label: 'Liste d\'attente' }
};

export const GestionDashboard = () => {
  const [applications, setApplications] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [sessionForm, setSessionForm] = useState({
    course_id: '',
    start_date: '',
    end_date: '',
    available_spots: '',
    location: 'Istres'
  });

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [appsData, sessionsData, coursesData] = await Promise.all([
        api.getAllApplications(statusFilter || null),
        api.getSessions(),
        api.getCourses({ course_type: 'onsite' })
      ]);
      setApplications(appsData);
      setSessions(sessionsData);
      setCourses(coursesData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (appId, status) => {
    try {
      await api.updateApplicationStatus(appId, status);
      toast.success('Statut mis à jour');
      loadData();
      setSelectedApp(null);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    try {
      await api.createSession({
        ...sessionForm,
        available_spots: parseInt(sessionForm.available_spots)
      });
      toast.success('Session créée');
      setShowSessionModal(false);
      setSessionForm({
        course_id: '',
        start_date: '',
        end_date: '',
        available_spots: '',
        location: 'Istres'
      });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Supprimer cette session ?')) return;
    try {
      await api.deleteSession(sessionId);
      toast.success('Session supprimée');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredApplications = applications.filter(app => {
    const user = app.user;
    if (!user) return false;
    return (
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.last_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const pendingCount = applications.filter(a => a.status === 'pending').length;

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-beige border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-semibold mb-2" data-testid="gestion-title">
              Gestion administrative
            </h1>
            <p className="text-muted-foreground">
              Gérez les candidatures et les sessions de formation
            </p>
          </div>
          {pendingCount > 0 && (
            <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30 text-base px-4 py-2">
              {pendingCount} candidature{pendingCount > 1 ? 's' : ''} en attente
            </Badge>
          )}
        </div>

        <Tabs defaultValue="applications" className="space-y-6">
          <TabsList className="bg-white border border-[#1F2A4A]/10">
            <TabsTrigger value="applications" className="data-[state=active]:bg-[#C9B896] data-[state=active]:text-black">
              <FileText className="w-4 h-4 mr-2" />
              Candidatures ({applications.length})
            </TabsTrigger>
            <TabsTrigger value="sessions" className="data-[state=active]:bg-[#C9B896] data-[state=active]:text-black">
              <Calendar className="w-4 h-4 mr-2" />
              Sessions ({sessions.length})
            </TabsTrigger>
          </TabsList>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par nom ou email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-white border border-[#1F2A4A]/10"
                  data-testid="search-applications"
                />
              </div>
              <Select value={statusFilter || "all"} onValueChange={(val) => setStatusFilter(val === "all" ? "" : val)}>
                <SelectTrigger className="w-full md:w-48 bg-white border border-[#1F2A4A]/10">
                  <SelectValue placeholder="Tous les statuts" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-[#1F2A4A]/10">
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="accepted">Acceptée</SelectItem>
                  <SelectItem value="rejected">Refusée</SelectItem>
                  <SelectItem value="waitlist">Liste d'attente</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-4">
              {filteredApplications.length === 0 ? (
                <div className="text-center py-12 bg-white border border-[#1F2A4A]/10 rounded-lg">
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Aucune candidature trouvée</p>
                </div>
              ) : (
                filteredApplications.map((app) => {
                  const status = statusColors[app.status] || statusColors.pending;
                  const user = app.user;
                  const course = app.course;

                  return (
                    <div 
                      key={app.id}
                      className="bg-white border border-[#1F2A4A]/10 rounded-lg p-6"
                      data-testid={`application-card-${app.id}`}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Badge className={`${status.bg} ${status.text} border-none`}>
                              {status.label}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              {format(parseISO(app.created_at), 'dd MMM yyyy à HH:mm', { locale: fr })}
                            </span>
                          </div>
                          <h3 className="font-semibold text-lg mb-1">
                            {user?.first_name} {user?.last_name}
                          </h3>
                          <p className="text-muted-foreground text-sm">{user?.email}</p>
                          <p className="text-sm mt-2">
                            <span className="text-muted-foreground">Formation : </span>
                            {course?.title || 'Non spécifiée'}
                          </p>
                          {app.funding_source && (
                            <p className="text-sm">
                              <span className="text-muted-foreground">Financement : </span>
                              {app.funding_source}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setSelectedApp(app)}
                            data-testid={`view-application-${app.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Voir
                          </Button>
                          {app.status === 'pending' && (
                            <>
                              <Button 
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleUpdateStatus(app.id, 'accepted')}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Accepter
                              </Button>
                              <Button 
                                size="sm"
                                variant="destructive"
                                onClick={() => handleUpdateStatus(app.id, 'rejected')}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Refuser
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Application Detail Modal */}
            <Dialog open={!!selectedApp} onOpenChange={() => setSelectedApp(null)}>
              <DialogContent className="bg-white border border-[#1F2A4A]/10 max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">
                    Détails de la candidature
                  </DialogTitle>
                </DialogHeader>
                {selectedApp && (
                  <div className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground">Candidat</Label>
                        <p className="font-medium">{selectedApp.user?.first_name} {selectedApp.user?.last_name}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Email</Label>
                        <p className="font-medium">{selectedApp.user?.email}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Téléphone</Label>
                        <p className="font-medium">{selectedApp.user?.phone || 'Non renseigné'}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Financement</Label>
                        <p className="font-medium">{selectedApp.funding_source || 'Non renseigné'}</p>
                      </div>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Motivation</Label>
                      <p className="mt-1 p-3 bg-navy-light rounded-lg">{selectedApp.motivation}</p>
                    </div>
                    {selectedApp.experience && (
                      <div>
                        <Label className="text-muted-foreground">Expérience</Label>
                        <p className="mt-1 p-3 bg-navy-light rounded-lg">{selectedApp.experience}</p>
                      </div>
                    )}
                    {selectedApp.status === 'pending' && (
                      <div className="flex gap-3 pt-4">
                        <Button 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleUpdateStatus(selectedApp.id, 'accepted')}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Accepter
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={() => handleUpdateStatus(selectedApp.id, 'waitlist')}
                        >
                          <Clock className="w-4 h-4 mr-2" />
                          Liste d'attente
                        </Button>
                        <Button 
                          variant="destructive"
                          onClick={() => handleUpdateStatus(selectedApp.id, 'rejected')}
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Refuser
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Sessions Tab */}
          <TabsContent value="sessions">
            <div className="flex justify-end mb-4">
              <Button 
                className="bg-[#C9B896] text-black hover:bg-[#E0D4BA]"
                onClick={() => setShowSessionModal(true)}
                data-testid="create-session-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle session
              </Button>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {sessions.map((session) => {
                const course = courses.find(c => c.id === session.course_id);
                const spotsLeft = session.available_spots - session.enrolled_count;

                return (
                  <div 
                    key={session.id}
                    className="bg-white border border-[#1F2A4A]/10 rounded-lg p-6"
                    data-testid={`session-card-${session.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold">{course?.title || 'Formation'}</h3>
                        <Badge className="mt-1" variant={spotsLeft > 0 ? 'default' : 'destructive'}>
                          {spotsLeft} places restantes
                        </Badge>
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-red-500"
                        onClick={() => handleDeleteSession(session.id)}
                      >
                        Supprimer
                      </Button>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-[#C9B896]" />
                        <span>
                          {format(parseISO(session.start_date), 'dd MMM yyyy', { locale: fr })} - {format(parseISO(session.end_date), 'dd MMM yyyy', { locale: fr })}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[#C9B896]" />
                        <span>{session.location}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-[#C9B896]" />
                        <span>{session.enrolled_count} / {session.available_spots} inscrits</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Create Session Modal */}
            <Dialog open={showSessionModal} onOpenChange={setShowSessionModal}>
              <DialogContent className="bg-white border border-[#1F2A4A]/10">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">
                    Créer une session
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateSession} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Formation</Label>
                    <Select
                      value={sessionForm.course_id}
                      onValueChange={(val) => setSessionForm({ ...sessionForm, course_id: val })}
                    >
                      <SelectTrigger className="bg-white border border-[#1F2A4A]/10">
                        <SelectValue placeholder="Sélectionner une formation" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-[#1F2A4A]/10">
                        {courses.map((course) => (
                          <SelectItem key={course.id} value={course.id}>{course.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date de début</Label>
                      <Input
                        type="datetime-local"
                        value={sessionForm.start_date}
                        onChange={(e) => setSessionForm({ ...sessionForm, start_date: e.target.value })}
                        className="bg-white border border-[#1F2A4A]/10"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Date de fin</Label>
                      <Input
                        type="datetime-local"
                        value={sessionForm.end_date}
                        onChange={(e) => setSessionForm({ ...sessionForm, end_date: e.target.value })}
                        className="bg-white border border-[#1F2A4A]/10"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Places disponibles</Label>
                      <Input
                        type="number"
                        value={sessionForm.available_spots}
                        onChange={(e) => setSessionForm({ ...sessionForm, available_spots: e.target.value })}
                        className="bg-white border border-[#1F2A4A]/10"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Lieu</Label>
                      <Input
                        value={sessionForm.location}
                        onChange={(e) => setSessionForm({ ...sessionForm, location: e.target.value })}
                        className="bg-white border border-[#1F2A4A]/10"
                        required
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="bg-[#C9B896] text-black hover:bg-[#E0D4BA]">
                      Créer la session
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowSessionModal(false)}>
                      Annuler
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
